import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import {
  Package, ShoppingCart, DollarSign, Clock, CheckCircle2,
  TrendingUp, Activity, ArrowUpRight,
} from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { formatCurrency } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import {
  Area, AreaChart, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short",
    hour: "numeric", minute: "2-digit",
  });

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const PaymentBadge = ({ status, time }: { status: string; time?: string | null }) => {
  const isReceived = status === "received";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${
        isReceived ? "bg-success/15 text-success" : "bg-warning/15 text-warning-foreground"
      }`}
    >
      {isReceived ? <CheckCircle2 className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />}
      {isReceived && time ? `Received · ${formatTime(time)}` : status}
    </span>
  );
};

const AdminOverview = () => {
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const [stats, setStats] = useState({ products: 0, orders: 0, revenue: 0, pending: 0, completed: 0 });
  const [recent, setRecent] = useState<any[]>([]);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [prodRes, ordRes, recentRes] = await Promise.all([
        supabase.from("products").select("id", { count: "exact", head: true }),
        supabase.from("orders").select("total_amount, order_status, payment_status, received_at, created_at"),
        supabase.from("orders").select("*").order("created_at", { ascending: false }).limit(8),
      ]);
      const orders = ordRes.data ?? [];
      const revenue = orders
        .filter((o) => o.payment_status === "received")
        .reduce((s, o) => s + Number(o.total_amount), 0);
      const pending = orders.filter((o) => o.payment_status !== "received").length;
      const completed = orders.filter((o) => o.order_status === "delivered").length;
      setStats({ products: prodRes.count ?? 0, orders: orders.length, revenue, pending, completed });
      setRecent(recentRes.data ?? []);
      setAllOrders(orders);
      setLoading(false);
    };
    load();
  }, []);

  // Build last-7-days revenue + orders chart
  const chartData = useMemo(() => {
    const days: { day: string; revenue: number; orders: number; key: string }[] = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      d.setHours(0, 0, 0, 0);
      days.push({
        key: d.toISOString().slice(0, 10),
        day: d.toLocaleDateString(undefined, { weekday: "short" }),
        revenue: 0,
        orders: 0,
      });
    }
    const map = new Map(days.map((d) => [d.key, d]));
    for (const o of allOrders) {
      const created = (o.created_at as string).slice(0, 10);
      const bucket = map.get(created);
      if (bucket) bucket.orders += 1;
      if (o.payment_status === "received" && o.received_at) {
        const rec = (o.received_at as string).slice(0, 10);
        const rb = map.get(rec);
        if (rb) rb.revenue += Number(o.total_amount);
      }
    }
    return days;
  }, [allOrders]);

  // Activity feed: combine new orders + received payments, most recent first
  const activity = useMemo(() => {
    const events: { type: "order" | "payment"; at: string; name: string; amount?: number }[] = [];
    for (const o of recent) {
      events.push({ type: "order", at: o.created_at, name: o.customer_name });
      if (o.payment_status === "received" && o.received_at) {
        events.push({
          type: "payment",
          at: o.received_at,
          name: o.customer_name,
          amount: Number(o.total_amount),
        });
      }
    }
    return events.sort((a, b) => +new Date(b.at) - +new Date(a.at)).slice(0, 6);
  }, [recent]);

  const cards = [
    {
      label: "Total revenue", value: formatCurrency(stats.revenue, symbol),
      icon: DollarSign, accent: "text-success", chip: "bg-success/10",
    },
    {
      label: "Total orders", value: stats.orders,
      icon: ShoppingCart, accent: "text-primary", chip: "bg-primary/10",
    },
    {
      label: "Pending payments", value: stats.pending,
      icon: Clock, accent: "text-warning-foreground", chip: "bg-warning/15",
    },
    {
      label: "Completed orders", value: stats.completed,
      icon: CheckCircle2, accent: "text-accent", chip: "bg-accent/10",
    },
    {
      label: "Total products", value: stats.products,
      icon: Package, accent: "text-foreground", chip: "bg-secondary",
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold">Overview</h1>
        <p className="text-muted-foreground">Your store at a glance</p>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)
          : cards.map((c) => (
              <Card
                key={c.label}
                className="group p-5 border-0 shadow-soft hover:shadow-card hover:-translate-y-0.5 transition-smooth"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    {c.label}
                  </p>
                  <div className={`grid h-9 w-9 place-items-center rounded-full ${c.chip} ${c.accent}`}>
                    <c.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="font-display text-2xl xl:text-3xl font-bold">{c.value}</p>
              </Card>
            ))}
      </div>

      {/* Charts row */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="border-0 shadow-soft p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Revenue
              </p>
              <h2 className="font-display text-xl font-semibold flex items-center gap-2">
                Last 7 days
                <TrendingUp className="h-4 w-4 text-success" />
              </h2>
            </div>
          </div>
          <div className="h-56 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={40} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "0.5rem",
                    fontSize: "12px",
                  }}
                  formatter={(v: number) => [formatCurrency(v, symbol), "Revenue"]}
                />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="border-0 shadow-soft p-5">
          <div className="mb-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Orders
            </p>
            <h2 className="font-display text-xl font-semibold">Per day</h2>
          </div>
          <div className="h-56 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "0.5rem",
                    fontSize: "12px",
                  }}
                />
                <Bar dataKey="orders" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Activity + Recent orders */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-0 shadow-soft">
          <div className="flex items-center justify-between p-5 border-b">
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary">
                <Activity className="h-4 w-4" />
              </div>
              <h2 className="font-display text-xl font-semibold">Recent activity</h2>
            </div>
          </div>
          <div className="divide-y">
            {activity.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">No activity yet.</div>
            ) : (
              activity.map((e, i) => (
                <div key={i} className="flex items-center gap-3 p-4">
                  <div
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${
                      e.type === "payment"
                        ? "bg-success/15 text-success"
                        : "bg-primary/10 text-primary"
                    }`}
                  >
                    {e.type === "payment" ? (
                      <DollarSign className="h-4 w-4" />
                    ) : (
                      <ShoppingCart className="h-4 w-4" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      {e.type === "payment" ? (
                        <>
                          Payment received from <span className="font-medium">{e.name}</span>
                          {e.amount !== undefined && (
                            <> — <span className="font-medium">{formatCurrency(e.amount, symbol)}</span></>
                          )}
                        </>
                      ) : (
                        <>
                          New order placed by <span className="font-medium">{e.name}</span>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(e.at)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="border-0 shadow-soft">
          <div className="flex items-center justify-between p-5 border-b">
            <h2 className="font-display text-xl font-semibold">Recent orders</h2>
            <Link
              to="/admin/orders"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y">
            {recent.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">No orders yet.</div>
            ) : (
              recent.slice(0, 6).map((o) => (
                <Link
                  key={o.id}
                  to="/admin/orders"
                  className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-smooth"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{o.customer_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-muted-foreground font-mono">
                        #{o.id.slice(0, 8)}
                      </p>
                      <PaymentBadge status={o.payment_status} time={o.received_at} />
                    </div>
                  </div>
                  <span className="font-display font-semibold shrink-0">
                    {formatCurrency(Number(o.total_amount), symbol)}
                  </span>
                </Link>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverview;
